import { Point2D as Point2DInterface } from '@paragon/contract-library';
import {
  degreesToSlope,
  Direction2D,
  distanceLessThan,
  distanceLessThanOrEqual,
  Point2D,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { resetMousyInsertionProfile, setMousyInsertionProfile } from '../../store/mousyInsertionProfileStore';
import { MousyProfileInsertionInformation } from '../../store/selectors/mousyProfileInsertionSelectors';
import { SnappingLevel } from '../../store/slices/applicationSlice';
import { getDistanceSnap } from '../../three/hooks/useMousePositionWithBasicInsertionSnapping';
import { ProfileClass } from './ProfileClass';
import {
  addNextLineToProfileFromUserInput,
  getHeelHeightBasedOnAdjacentTopChordPoint,
  getHeelHeightBasedOnSlope,
} from './profileManipulation';

export function updateMousyInsertionProfile(
  mousePosition: Point2DInterface,
  { profile, topChordWidth, snappingLevel }: MousyProfileInsertionInformation,
): void {
  const snapResult = snapMousePositionDuringProfileInsertion(profile, mousePosition, topChordWidth, snappingLevel);
  if (snapResult == null) return;

  const updatedProfile = addNextLineToProfileFromUserInput({ profile, topChordWidth }, snapResult);

  if (updatedProfile) {
    const updatedProfileTrussSettings = { ...profile.settingsData, profile: updatedProfile };
    const isProfileValid = ProfileClass.create(updatedProfileTrussSettings).type === 'Ok';
    // Just ignore the change if it's not valid, since this is just a preview visual that is updated on mouse move.
    // We'll show an error to the user if they try to submit these changes (by clicking).
    if (isProfileValid) {
      setMousyInsertionProfile(updatedProfile);
    }
  } else {
    resetMousyInsertionProfile();
  }
}

export function snapMousePositionDuringProfileInsertion(
  profile: ProfileClass,
  mousePosition: Point2DInterface,
  topChordWidth: number,
  snappingLevel: SnappingLevel,
) {
  const { span, workingHalf } = profile;

  if (workingHalf == null) return null;

  const lastProfileLine = profile.getLastProfileLine();

  const isFirstBottomLine =
    workingHalf === 'Bottom' && profile.leftSeatCut == null && profile.bottomChords.length === 0;
  if (isFirstBottomLine) console.assert(profile.rightSeatCut == null, 'There should be no right seat cut.');

  let lastJointPoint;

  if (workingHalf === 'Top') {
    if (!lastProfileLine) {
      const snappedSlopeFromOrigin = snapSlope(degreesToSlope(Direction2D.fromPoint(mousePosition).angle));
      if (snappedSlopeFromOrigin === 90 || distanceLessThanOrEqual(mousePosition.x, 0)) {
        const verticalDistance = snapDistance(mousePosition.y, snappingLevel);
        return verticalDistance > 0
          ? { slope: verticalDistance < 0 ? -90 : 90, distance: Math.abs(verticalDistance) }
          : null;
      }
    }

    // Now that we've eliminated the case of the user just putting in a left heel, we can use the last inserted point or
    // the left heel end point as the base point for the next line being inserted.

    if (lastProfileLine) {
      lastJointPoint = lastProfileLine.endPoint;
    } else {
      const minLeftHeelHeight = getHeelHeightBasedOnSlope(profile.profileData.leftHeelSettings, 0, topChordWidth);
      if (distanceLessThanOrEqual(mousePosition.y, minLeftHeelHeight)) {
        lastJointPoint = new Point2D(0, minLeftHeelHeight);
      } else {
        // The heel won't be 100% accurate after snapping, but it shouldn't make a noticeable difference.
        const leftHeelHeightBasedOnAdjacentPoint = getHeelHeightBasedOnAdjacentTopChordPoint(
          profile,
          Point2D.fromObject(mousePosition),
          topChordWidth,
          'Left',
        );
        // We clamp the heel height to the minimum heel height in case the mouse position would lead to a negative slope.
        const clampedHeelHeight =
          mousePosition.y < minLeftHeelHeight ? minLeftHeelHeight : leftHeelHeightBasedOnAdjacentPoint;
        lastJointPoint = new Point2D(0, clampedHeelHeight);
      }
    }

    const rightHeelHeight = getHeelHeightBasedOnAdjacentTopChordPoint(profile, lastJointPoint, topChordWidth, 'Right');
    if (distanceLessThanOrEqual(mousePosition.y, rightHeelHeight)) {
      // Snap to right heel
      const rightHeelSnapPoint = new Point2D(span, rightHeelHeight);
      const directionToRightHeel = Direction2D.fromPoints(lastJointPoint, rightHeelSnapPoint);
      const slopeToRightHeel = degreesToSlope(directionToRightHeel.angle);
      return { slope: slopeToRightHeel, distance: span - lastJointPoint.x };
    }
  } else if (workingHalf === 'Bottom') {
    if (distanceLessThan(mousePosition.y, 0)) {
      const spanPoint = new Point2D(span, 0);
      lastJointPoint = isFirstBottomLine ? Point2D.origin : lastProfileLine!.endPoint;
      const directionToSpanPoint = Direction2D.fromPoints(lastJointPoint, spanPoint);
      const slope = snapSlope(degreesToSlope(directionToSpanPoint.angle));

      if (slope === 0) {
        // Snap to span
        const slopeToRightHeel = degreesToSlope(directionToSpanPoint.angle);
        return { slope: slopeToRightHeel, distance: span - lastJointPoint.x };
      }

      // Snap to seat cut
      const seatCutStartX = span - profile.settingsData.rightSeatCut;
      const seatCutPoint = new Point2D(seatCutStartX, 0);
      const directionToRightSeatCut = Direction2D.fromPoints(lastJointPoint, seatCutPoint);
      const slopeToRightSeatCut = degreesToSlope(directionToRightSeatCut.angle);
      return { slope: slopeToRightSeatCut, distance: span - profile.settingsData.rightSeatCut - lastJointPoint.x };
    }

    if (isFirstBottomLine) {
      const distanceFromOrigin = snapDistance(mousePosition.x, snappingLevel);
      const snappedSlopeFromOrigin = snapSlope(degreesToSlope(Direction2D.fromPoint(mousePosition).angle));

      if (distanceFromOrigin <= 0 || snappedSlopeFromOrigin >= 90) return null;
      if (snappedSlopeFromOrigin <= 0 || distanceLessThanOrEqual(distanceFromOrigin, profile.settingsData.leftSeatCut))
        return { slope: 0, distance: distanceFromOrigin };

      // We need to account for a seat cut
      const leftSeatCutEndPoint = new Point2D(profile.settingsData.leftSeatCut, 0);
      const directionFromSeatCut = Direction2D.fromPointObjects(leftSeatCutEndPoint, mousePosition);
      const slopeFromSeatCut = snapSlope(degreesToSlope(directionFromSeatCut.angle));
      return { slope: slopeFromSeatCut, distance: distanceFromOrigin - profile.settingsData.leftSeatCut };
    }

    lastJointPoint = lastProfileLine!.endPoint;
  } else {
    assertExhaustiveSwitch(workingHalf);
  }

  const horizontalDistance = snapDistance(mousePosition.x - lastJointPoint.x, snappingLevel);
  const direction = Direction2D.fromPointObjects(lastJointPoint, mousePosition);
  const slope = snapSlope(degreesToSlope(direction.angle));

  if (Math.abs(slope) < 90) {
    // A normal non-vertical chord
    return horizontalDistance > 0 ? { slope, distance: horizontalDistance } : null;
  }

  // A normal vertical chord
  const verticalDistance = snapDistance(mousePosition.y - lastJointPoint.y, snappingLevel);
  const absoluteVerticalDistance = Math.abs(verticalDistance);
  return absoluteVerticalDistance > 0
    ? { slope: verticalDistance < 0 ? -90 : 90, distance: absoluteVerticalDistance }
    : null;
}

const round = (value: number, { toNearest }: { toNearest: number }) => Math.round(value / toNearest) * toNearest;

function snapSlope(slope: number): number {
  if (Math.abs(slope) < 1) return round(slope, { toNearest: 0.25 });
  if (Math.abs(slope) < 12) return round(slope, { toNearest: 1 });
  if (Math.abs(slope) < 90) return round(slope, { toNearest: 2 });

  return 90 * Math.sign(slope);
}

function snapDistance(distance: number, level: SnappingLevel): number {
  return round(distance, { toNearest: getDistanceSnap(level) });
}
